import { NgTemplateOutlet } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, effect, inject, output } from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'

import { TooltipModule } from 'primeng/tooltip'

import { FlxFormControlModule } from '@flowx/angular-ui-toolkit'
import { FlxSpinnerModule } from '@flowx/designer/shared/components/spinner'
import {
  ConflictingDependencies,
  ConflictingDependency,
  ConflictingDependencyType,
} from '@flowx/designer/shared/model/copy-resource.model'
import { CopyResourceService } from '@flowx/designer/shared/services/copy-resource.service'

@Component({
  selector: 'flx-copy-resource-review-dependencies',
  templateUrl: 'copy-resource-review-dependencies.component.html',
  styleUrls: ['copy-resource-review-dependencies.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FlxFormControlModule,
    ReactiveFormsModule,
    TooltipModule,
    FlxSpinnerModule,
    NgTemplateOutlet,
  ],
  host: { class: 'overflow-auto' },
})
export class CopyResourceReviewDependenciesComponent {
  #copyResourceService = inject(CopyResourceService)

  protected readonly ConflictingDependencyType = ConflictingDependencyType

  title = output<string>()
  subtitle = output<string>()

  resourceName = this.#copyResourceService.resourceName
  targetAppName = this.#copyResourceService.applicationFC.value.name
  dependenciesComparison = this.#copyResourceService.dependenciesComparison
  applicationFC = this.#copyResourceService.applicationFC

  conflictingDependencies = computed<ConflictingDependencies>(() => {
    const { conflictingDependencies } = this.dependenciesComparison()
    const { id: targetAppId } = this.applicationFC.value

    if (!conflictingDependencies.length) {
      return {
        usualConflicts: [],
        circularConflicts: [],
      }
    }

    return conflictingDependencies.reduce(
      (acc: ConflictingDependencies, conflictingDependency: ConflictingDependency) => {
        if (conflictingDependency.buildAppId !== targetAppId) {
          acc.usualConflicts.push(conflictingDependency)
        } else {
          acc.circularConflicts.push(conflictingDependency)
        }

        return acc
      },
      { usualConflicts: [], circularConflicts: [] } satisfies ConflictingDependencies
    )
  })

  newDependencies = computed(() => {
    const dependencyComparison = this.dependenciesComparison()

    if (dependencyComparison?.newDependencies.length) {
      return dependencyComparison.newDependencies
    }

    return []
  })

  hasConflicts = computed(() => {
    const conflictingDependencies = this.conflictingDependencies()

    return (
      conflictingDependencies.usualConflicts.length ||
      conflictingDependencies.circularConflicts.length
    )
  })

  constructor() {
    effect(() => {
      const hasConflicts = this.hasConflicts()
      const hasNewDependencies = this.newDependencies().length

      if (hasConflicts && hasNewDependencies) {
        this.title.emit('Dependency Changes')
      } else if (hasConflicts) {
        this.title.emit('Dependency Conflicts Detected')
      } else if (hasNewDependencies) {
        this.subtitle.emit(
          'Copying this process will add below dependencies to destination project.'
        )
      }
    })
  }
}
